import { motion } from "framer-motion";

export function HeroIllustration() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
      style={{ perspective: "1200px" }}
    >
      {/* Ambient glow behind */}
      <div className="absolute -inset-8 rounded-3xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent blur-[60px] opacity-80" />

      {/* Main device frame with 3D tilt */}
      <motion.div
        className="relative"
        initial={{ rotateX: 8, rotateY: -6, scale: 0.92 }}
        animate={{ rotateX: 4, rotateY: -3, scale: 1 }}
        transition={{ duration: 1.5, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* Shadow layer */}
        <div className="absolute -bottom-6 inset-x-4 h-16 bg-gradient-to-b from-black/8 to-transparent blur-2xl rounded-3xl" />

        {/* Browser chrome */}
        <div className="relative rounded-2xl border border-border/60 bg-gradient-to-b from-muted/90 to-card shadow-2xl shadow-primary/5 overflow-hidden backdrop-blur-sm">
          {/* Title bar */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border/40 bg-muted/60">
            <div className="flex gap-1.5">
              <span className="w-3 h-3 rounded-full bg-destructive/60" />
              <span className="w-3 h-3 rounded-full bg-yellow-400/60" />
              <span className="w-3 h-3 rounded-full bg-green-500/60" />
            </div>
            <div className="flex-1 flex justify-center">
              <div className="flex items-center gap-2 px-4 py-1 rounded-lg bg-background/70 border border-border/30 text-[10px] text-muted-foreground/60 font-medium tracking-wide">
                <svg width="10" height="10" viewBox="0 0 16 16" fill="none" className="text-green-500/70">
                  <path d="M8 1L8 1C4.13 1 1 4.13 1 8s3.13 7 7 7 7-3.13 7-7S11.87 1 8 1z" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                app.ouratrust.com/dashboard
              </div>
            </div>
            <div className="w-16" />
          </div>

          {/* Dashboard content */}
          <div className="p-4 bg-gradient-to-br from-background via-background to-muted/30" style={{ minHeight: "280px" }}>
            {/* Top bar */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-primary/15 flex items-center justify-center">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-primary">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" />
                  </svg>
                </div>
                <div className="h-3 w-20 rounded bg-foreground/10" />
              </div>
              <div className="flex gap-2">
                <div className="h-7 w-16 rounded-lg bg-primary/10 border border-primary/15" />
                <div className="h-7 w-7 rounded-lg bg-muted border border-border/40" />
              </div>
            </div>

            {/* KPI Cards row */}
            <motion.div
              className="grid grid-cols-4 gap-2.5 mb-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.7 }}
            >
              {/* KPI 1 - Rating */}
              <div className="rounded-xl bg-card border border-border/30 p-3 shadow-sm">
                <div className="flex items-center gap-1 mb-1.5">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} width="8" height="8" viewBox="0 0 24 24" fill="hsl(40, 90%, 55%)" className="drop-shadow-sm">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <div className="text-sm font-bold text-foreground/85 tracking-tight">4.8</div>
                <div className="text-[9px] text-muted-foreground/50 mt-0.5">Note moyenne</div>
              </div>

              {/* KPI 2 - Reviews */}
              <div className="rounded-xl bg-card border border-border/30 p-3 shadow-sm">
                <div className="flex items-center gap-1 mb-1.5">
                  <div className="w-4 h-4 rounded bg-primary/12 flex items-center justify-center">
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" className="text-primary">
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="currentColor" strokeWidth="2.5" />
                    </svg>
                  </div>
                </div>
                <div className="text-sm font-bold text-foreground/85 tracking-tight">1,247</div>
                <div className="text-[9px] text-muted-foreground/50 mt-0.5">Avis gérés</div>
              </div>

              {/* KPI 3 - Response rate */}
              <div className="rounded-xl bg-card border border-border/30 p-3 shadow-sm">
                <div className="flex items-center gap-1 mb-1.5">
                  <div className="w-4 h-4 rounded bg-green-500/12 flex items-center justify-center">
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" className="text-green-600">
                      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                      <path d="M22 4L12 14.01l-3-3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
                <div className="text-sm font-bold text-foreground/85 tracking-tight">98%</div>
                <div className="text-[9px] text-muted-foreground/50 mt-0.5">Taux de réponse</div>
              </div>

              {/* KPI 4 - Time saved */}
              <div className="rounded-xl bg-card border border-border/30 p-3 shadow-sm">
                <div className="flex items-center gap-1 mb-1.5">
                  <div className="w-4 h-4 rounded bg-orange-500/12 flex items-center justify-center">
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" className="text-orange-500">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" />
                      <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  </div>
                </div>
                <div className="text-sm font-bold text-foreground/85 tracking-tight">12h</div>
                <div className="text-[9px] text-muted-foreground/50 mt-0.5">Temps gagné/sem</div>
              </div>
            </motion.div>

            {/* Chart area */}
            <motion.div
              className="rounded-xl bg-card border border-border/30 p-3 shadow-sm"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.7 }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-semibold text-foreground/80">Note Google</span>
                  <div className="flex items-center gap-0.5 text-[8px] text-green-600 font-bold bg-green-500/10 px-1.5 py-0.5 rounded-full">
                    <svg width="6" height="6" viewBox="0 0 12 12" fill="currentColor">
                      <path d="M6 1L10 7H2L6 1Z" />
                    </svg>
                    +0.8
                  </div>
                </div>
                <div className="flex gap-1">
                  <div className="h-5 w-10 rounded bg-muted/80 border border-border/20 text-[7px] text-muted-foreground/50 flex items-center justify-center">7j</div>
                  <div className="h-5 w-12 rounded bg-primary/10 border border-primary/20 text-[7px] text-primary/80 flex items-center justify-center font-medium">6 mois</div>
                </div>
              </div>

              {/* SVG Chart - Google Rating evolution */}
              <svg viewBox="0 0 320 110" className="w-full" fill="none">
                <defs>
                  <linearGradient id="chartAreaGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="hsl(220, 80%, 55%)" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="hsl(220, 80%, 55%)" stopOpacity="0.02" />
                  </linearGradient>
                  <linearGradient id="barGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="hsl(220, 75%, 55%)" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="hsl(225, 70%, 60%)" stopOpacity="0.55" />
                  </linearGradient>
                  <linearGradient id="barGradGreen" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="hsl(155, 70%, 42%)" stopOpacity="0.95" />
                    <stop offset="100%" stopColor="hsl(160, 60%, 48%)" stopOpacity="0.6" />
                  </linearGradient>
                </defs>

                {/* Y-axis labels (rating scale) */}
                <text x="2" y="14" fontSize="7" fill="hsl(220, 20%, 60%)" fontFamily="system-ui" fontWeight="500">5.0</text>
                <text x="2" y="38" fontSize="7" fill="hsl(220, 20%, 60%)" fontFamily="system-ui" fontWeight="500">4.5</text>
                <text x="2" y="62" fontSize="7" fill="hsl(220, 20%, 60%)" fontFamily="system-ui" fontWeight="500">4.0</text>
                <text x="2" y="86" fontSize="7" fill="hsl(220, 20%, 60%)" fontFamily="system-ui" fontWeight="500">3.5</text>

                {/* Grid lines */}
                <line x1="22" y1="10" x2="315" y2="10" stroke="hsl(220, 20%, 88%)" strokeWidth="0.5" strokeOpacity="0.5" />
                <line x1="22" y1="34" x2="315" y2="34" stroke="hsl(220, 20%, 88%)" strokeWidth="0.5" strokeOpacity="0.5" />
                <line x1="22" y1="58" x2="315" y2="58" stroke="hsl(220, 20%, 88%)" strokeWidth="0.5" strokeOpacity="0.5" />
                <line x1="22" y1="82" x2="315" y2="82" stroke="hsl(220, 20%, 88%)" strokeWidth="0.5" strokeOpacity="0.5" />

                {/* Bar chart - rating per month mapped: 3.5→82, 4.0→58, 4.5→34, 5.0→10 */}
                {/* Ratings: 3.8, 3.9, 4.0, 4.1, 4.3, 4.6 → y: 72, 67, 58, 53, 43, 29 */}
                <motion.g
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2, duration: 0.6 }}
                >
                  {[
                    { x: 35, y: 72, h: 20, label: "Oct" },
                    { x: 82, y: 67, h: 25, label: "Nov" },
                    { x: 129, y: 58, h: 34, label: "Déc" },
                    { x: 176, y: 53, h: 39, label: "Jan" },
                    { x: 223, y: 43, h: 49, label: "Fév" },
                    { x: 270, y: 29, h: 63, label: "Mar" },
                  ].map((bar, i) => (
                    <g key={i}>
                      <motion.rect
                        x={bar.x}
                        y={bar.y}
                        width="30"
                        height={bar.h}
                        rx="4"
                        fill={i === 5 ? "url(#barGradGreen)" : "url(#barGrad)"}
                        initial={{ height: 0, y: 92 }}
                        animate={{ height: bar.h, y: bar.y }}
                        transition={{ delay: 1.3 + i * 0.08, duration: 0.5, ease: "easeOut" }}
                      />
                      {/* Month labels */}
                      <text x={bar.x + 15} y="102" fontSize="7" fill="hsl(220, 20%, 55%)" fontFamily="system-ui" textAnchor="middle" fontWeight="500">{bar.label}</text>
                    </g>
                  ))}
                </motion.g>

                {/* Trend line through bar tops */}
                <motion.path
                  d="M50 72 L97 67 L144 58 L191 53 L238 43 L285 29"
                  stroke="hsl(220, 80%, 50%)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ delay: 1.8, duration: 1, ease: "easeOut" }}
                />

                {/* Area fill */}
                <motion.path
                  d="M50 72 L97 67 L144 58 L191 53 L238 43 L285 29 L285 92 L50 92 Z"
                  fill="url(#chartAreaGrad)"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2, duration: 0.8 }}
                />

                {/* Rating value labels on bars */}
                <motion.g
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2.2, duration: 0.5 }}
                >
                  <text x="50" y="68" fontSize="7" fill="hsl(220, 50%, 40%)" fontFamily="system-ui" fontWeight="700" textAnchor="middle">3.8</text>
                  <text x="285" y="25" fontSize="8" fill="hsl(220, 70%, 45%)" fontFamily="system-ui" fontWeight="800" textAnchor="middle">4.6</text>
                </motion.g>

              </svg>
            </motion.div>

            {/* Bottom review row hint */}
            <motion.div
              className="mt-3 flex gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 0.5 }}
            >
              <div className="flex-1 rounded-lg bg-card border border-border/25 p-2 flex items-center gap-2 shadow-sm">
                <div className="w-6 h-6 rounded-full bg-primary/10 shrink-0" />
                <div className="flex-1 space-y-1">
                  <div className="h-2 w-20 rounded bg-foreground/8" />
                  <div className="h-1.5 w-32 rounded bg-foreground/5" />
                </div>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} width="6" height="6" viewBox="0 0 24 24" fill={i < 5 ? "hsl(40, 90%, 55%)" : "hsl(220, 10%, 88%)"}>
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                    </svg>
                  ))}
                </div>
              </div>
              <div className="flex-1 rounded-lg bg-card border border-border/25 p-2 flex items-center gap-2 shadow-sm">
                <div className="w-6 h-6 rounded-full bg-green-500/10 shrink-0" />
                <div className="flex-1 space-y-1">
                  <div className="h-2 w-16 rounded bg-foreground/8" />
                  <div className="h-1.5 w-28 rounded bg-foreground/5" />
                </div>
                <div className="text-[8px] text-green-600 font-medium bg-green-500/8 px-1.5 py-0.5 rounded-full">✓ Répondu</div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Reflection / glass highlight */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none" />
      </motion.div>
    </motion.div>
  );
}
